import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Modal, TouchableWithoutFeedback, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { AntDesign } from '@expo/vector-icons';
import { colors } from '../../style/rulesEngine';
import useGreenScrollbar from './useGreenScrollbar';

/**
 * Dropdown matching the Figma design and receive/recount workflow style:
 * - Transparent background with bottom border only
 * - Options rendered in a full-screen Modal so they overlay everything
 *   without pushing down sibling elements or being clipped by parents
 */
const RuleEngineDropdown = ({
  options = [],
  selectedValue,
  onSelect,
  placeholder = 'Select...',
  testID,
  error = false,
  emptyMessage,
  multiSelect = false,
  selectedValues = [],
  triggerStyle,
  accessibilityLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = React.useRef(null);
  useGreenScrollbar('ruleEngineDropdownScroll');

  // Close when the PAGE scrolls, but NOT when the dropdown's own list is scrolled.
  // React Native Web sets data-testid on the ScrollView's DOM node, so we can
  // identify scrolls that originate from within the dropdown and ignore them.
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (e.target?.closest?.('[data-testid="ruleEngineDropdownScroll"]')) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  const selected = options.find((o) => o.value === selectedValue);

  const multiSelectLabel = multiSelect && selectedValues.length > 0
    ? options
        .filter((o) => selectedValues.includes(o.value))
        .map((o) => o.label)
        .join(', ')
    : null;

  const openDropdown = () => {
    setOpen(true);
    triggerRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setDropPos({ top: pageY + height + 2, left: pageX, width });
    });
  };

  const triggerBaseStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    borderBottomWidth: 2,
    borderBottomColor: error ? colors.danger : '#D9D9D9',
    paddingVertical: 7,
    paddingHorizontal: 2,
  };

  return (
    <View ref={triggerRef}>
      {/* Trigger */}
      <TouchableOpacity
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        onPress={openDropdown}
        style={triggerStyle ? [triggerBaseStyle, triggerStyle] : triggerBaseStyle}
        activeOpacity={0.7}
      >
        <Text
          style={{
            flex: 1,
            color: (multiSelect ? selectedValues.length > 0 : !!selected) ? '#ffffff' : colors.textMuted,
            fontFamily: 'Poppins_600SemiBold',
            fontSize: 16,
          }}
          numberOfLines={1}
        >
          {multiSelect ? (multiSelectLabel || placeholder) : (selected?.label || placeholder)}
        </Text>
        <AntDesign
          name={open ? 'up' : 'down'}
          size={11}
          color={colors.textMuted}
          style={{ marginLeft: 4 }}
        />
      </TouchableOpacity>

      {/* Options list — rendered in a Modal so it overlays without affecting layout */}
      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={() => setOpen(false)}
      >
        <TouchableWithoutFeedback onPress={() => setOpen(false)}>
          <View style={{ flex: 1 }}>
            <View
              style={{
                position: 'absolute',
                top: dropPos.top,
                left: dropPos.left,
                width: dropPos.width,
                zIndex: 9999,
                borderRadius: 6,
                overflow: 'hidden',
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.12)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.4,
                shadowRadius: 8,
                elevation: 20,
              }}
            >
              <LinearGradient colors={['#132131', '#1b374f']}>
                <ScrollView
                  testID="ruleEngineDropdownScroll"
                  style={{ maxHeight: 220 }}
                  bounces={false}
                  showsVerticalScrollIndicator={true}
                  keyboardShouldPersistTaps="handled"
                >
                  {options.length === 0 && emptyMessage ? (
                    <View
                      testID={testID ? `${testID}_empty` : 'ruleEngineDropdownEmpty'}
                      style={{ paddingVertical: 14, paddingHorizontal: 12 }}
                    >
                      <Text
                        style={{
                          color: colors.textMuted,
                          fontFamily: 'Poppins_500Medium',
                          fontSize: 13,
                          textAlign: 'center',
                        }}
                      >
                        {emptyMessage}
                      </Text>
                    </View>
                  ) : null}
                  {options.map((option) => {
                    const isSelected = multiSelect
                      ? selectedValues.includes(option.value)
                      : option.value === selectedValue;
                    return (
                      <TouchableOpacity
                        key={option.value}
                        testID={testID ? `${testID}_option_${option.value}` : undefined}
                        onPress={() => {
                          if (multiSelect) {
                            const next = isSelected
                              ? selectedValues.filter((v) => v !== option.value)
                              : [...selectedValues, option.value];
                            onSelect(next);
                          } else {
                            onSelect(option.value);
                            setOpen(false);
                          }
                        }}
                        style={{
                          paddingVertical: 10,
                          paddingHorizontal: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: isSelected ? 'rgba(72,90,114,0.5)' : 'transparent',
                          borderBottomWidth: 1,
                          borderBottomColor: 'rgba(255,255,255,0.06)',
                        }}
                      >
                        {multiSelect && (
                          <View
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 3,
                              borderWidth: 1.5,
                              borderColor: isSelected ? colors.accent : colors.textMuted,
                              backgroundColor: isSelected ? colors.accent : 'transparent',
                              marginRight: 10,
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {isSelected && (
                              <AntDesign name="check" size={10} color="#000" />
                            )}
                          </View>
                        )}
                        <Text
                          style={{
                            color: isSelected ? colors.accent : colors.text,
                            fontFamily: 'Poppins_500Medium',
                            fontSize: 13,
                          }}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </LinearGradient>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

export default RuleEngineDropdown;
